import { apiRequest } from '@/lib/api-client'

/**
 * `GET /mocktest/my-tasks/` (ASSIGNMENTS-SPEC 7.1, CONTRACT.md of school-onboarding-20261017): the signed-in student's tasks (the
 * welcome test their teacher chose for new students, then any other task) and the lock those set on other practice. The server is the authority (the start gate refuses a locked start with 409); this
 * module only reads what it says. Read defensively: an entry that is not what we expect is dropped, never shown half
 * formed, a lock we cannot read is no lock (the server still refuses the start), and an unknown key is ignored. Every id is a
 * string, like the rest of the app (`/test/<id>`).
 */

export type TaskKind = 'full_mock' | 'section' | 'specific_test'
export type TaskSkill = 'listening' | 'reading' | 'writing' | 'speaking'
export type TaskStatus = 'pending' | 'in_progress' | 'overdue' | 'completed' | 'waived'
/** Why practice is locked. The platform baseline is gone: a stray `baseline` reason from an older API reads as a teacher task. */
export type LockReason = 'onboarding' | 'teacher_task'
/** Why the server is not enforcing an open locking task right now (D11b, D11c). `unavailable` has its own boolean. */
export type LockSuspended = 'awaiting_marking' | 'speaking_missing'

export interface TaskTest {
  id: string
  title: string
  timeLimitMinutes: number | null
  /** Taskable now: the client never links to a paper that is not (C-m11). */
  available: boolean
}

export interface TaskLock {
  reason: LockReason
  taskId: string
  targetId: string
  requiredTestId: string | null
  allowedTestIds: string[]
  activeTestIds: string[]
  dueAt: Date | null
}

interface TaskFacts {
  targetId: string
  status: TaskStatus
  assignedAt: Date
  completedAt: Date | null
  test: TaskTest | null
  /**
   * The test behind this task cannot be started by this student right now (never-trap, R2): the lock is not enforced, the task
   * stays open, and the student reads that their teacher will pick a new test.
   */
  unavailable: boolean
  lastAttemptIncomplete: boolean
  lockSuspended: LockSuspended | null
}

/** A task a teacher set, or the first full mock they chose for every new student. */
export interface TaskItem extends TaskFacts {
  taskId: string
  /**
   * The welcome test: the one full mock a teacher chose for every student who joins the school. The API stores the English title
   * "Onboarding test"; the page says "Welcome test" itself, in the student's language.
   */
  onboarding: boolean
  title: string
  instructions: string
  kind: TaskKind
  skill: TaskSkill | null
  dueAt: Date | null
  mandatory: boolean
  lockOthers: boolean
  late: boolean
}

export interface MyTasks {
  enabled: boolean
  lock: TaskLock | null
  tasks: TaskItem[]
}

export const NO_TASKS: MyTasks = { enabled: false, lock: null, tasks: [] }

type Raw = Record<string, unknown>
const isRecord = (value: unknown): value is Raw => typeof value === 'object' && value !== null && !Array.isArray(value)
const text = (value: unknown): string => (typeof value === 'string' ? value.trim() : '')
const flag = (value: unknown): boolean => value === true
const KINDS: readonly string[] = ['full_mock', 'section', 'specific_test']
const SKILLS: readonly string[] = ['listening', 'reading', 'writing', 'speaking']
const STATUSES: readonly string[] = ['pending', 'in_progress', 'overdue', 'completed', 'waived']
const SUSPENDED: readonly string[] = ['awaiting_marking', 'speaking_missing']

/**
 * An id as text. A JSON number is read only while it is exact: test ids are 19 digits (above 2^53), a browser has already rounded
 * such a number by the time it gets here, and a link built from a rounded id opens a paper that does not exist. The API sends them
 * as strings; a number that cannot be exact is unreadable (`null`), never a wrong id.
 */
function id(value: unknown): string | null {
  if (typeof value === 'number') return Number.isSafeInteger(value) ? String(value) : null
  const out = text(value)
  return out === '' ? null : out
}

/** An id the API sent that this build cannot read exactly (as opposed to one it did not send). */
const unreadable = (value: unknown): boolean => value !== null && value !== undefined && id(value) === null

function date(value: unknown): Date | null {
  const out = text(value)
  if (out === '') return null
  const parsed = new Date(out)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

function ids(value: unknown): string[] {
  return Array.isArray(value) ? value.map(id).filter((entry): entry is string => entry !== null) : []
}

function parseTest(value: unknown): TaskTest | null {
  if (!isRecord(value)) return null
  const testId = id(value.id)
  const title = text(value.title)
  if (testId === null || title === '') return null
  const minutes = typeof value.time_limit_minutes === 'number' && value.time_limit_minutes > 0 ? value.time_limit_minutes : null
  return { id: testId, title, timeLimitMinutes: minutes, available: value.available !== false }
}

function parseFacts(raw: Raw): TaskFacts | null {
  const targetId = id(raw.target_id)
  const status = text(raw.status)
  const assignedAt = date(raw.assigned_at)
  if (targetId === null || !STATUSES.includes(status) || assignedAt === null) return null
  const suspended = text(raw.lock_suspended)
  return {
    targetId,
    status: status as TaskStatus,
    assignedAt,
    completedAt: date(raw.completed_at),
    test: parseTest(raw.test),
    unavailable: flag(raw.unavailable),
    lastAttemptIncomplete: flag(raw.last_attempt_incomplete),
    lockSuspended: SUSPENDED.includes(suspended) ? (suspended as LockSuspended) : null,
  }
}

function parseLock(value: unknown): TaskLock | null {
  if (!isRecord(value)) return null
  const raw = text(value.reason)
  const taskId = id(value.task_id)
  const targetId = id(value.target_id)
  // A lock whose reason this build has never heard of is no lock (the server still refuses the start); a leftover `baseline` is a task lock.
  const reason: LockReason | null = raw === 'onboarding' ? 'onboarding' : raw === 'teacher_task' || raw === 'baseline' ? 'teacher_task' : null
  if (reason === null || taskId === null || targetId === null) return null
  // A lock whose test ids cannot be read exactly is no lock: half a list would block the very papers that finish it, and the
  // server still refuses a wrong start.
  const listed = [value.allowed_test_ids, value.active_test_ids].flatMap((entry) => (Array.isArray(entry) ? entry : []))
  if (unreadable(value.required_test_id) || listed.some(unreadable)) return null
  return {
    reason,
    taskId,
    targetId,
    requiredTestId: id(value.required_test_id),
    allowedTestIds: ids(value.allowed_test_ids),
    activeTestIds: ids(value.active_test_ids),
    dueAt: date(value.due_at),
  }
}

function parseTask(value: unknown): TaskItem | null {
  if (!isRecord(value)) return null
  const facts = parseFacts(value)
  const taskId = id(value.task_id)
  const title = text(value.title)
  const kind = text(value.kind)
  if (!facts || taskId === null || title === '' || !KINDS.includes(kind)) return null
  const skill = text(value.skill)
  return {
    ...facts,
    taskId,
    // An answer older than the welcome test has no flag: an ordinary task.
    onboarding: flag(value.onboarding),
    title,
    instructions: text(value.instructions),
    kind: kind as TaskKind,
    skill: SKILLS.includes(skill) ? (skill as TaskSkill) : null,
    dueAt: date(value.due_at),
    mandatory: value.mandatory !== false,
    lockOthers: flag(value.lock_others),
    late: flag(value.late),
  }
}

export function parseMyTasks(data: unknown): MyTasks {
  if (!isRecord(data) || data.enabled !== true) return NO_TASKS
  const list = Array.isArray(data.tasks) ? data.tasks : []
  return {
    enabled: true,
    lock: parseLock(data.lock),
    tasks: list.map(parseTask).filter((task): task is TaskItem => task !== null),
  }
}

export const MY_TASKS_TIMEOUT_MS = 8_000

export async function fetchMyTasks(): Promise<MyTasks> {
  // A lookup that hangs must not hold the student on a skeleton (`TaskGate`): after this it fails, and a failure goes through.
  return parseMyTasks(await apiRequest<unknown>('/my-tasks/', { timeoutMs: MY_TASKS_TIMEOUT_MS }))
}
